/**
 * TS port of the legacy `computeExercise` (app.js @ baseline/phase0-start).
 * Pure progression/display logic for a single plan exercise. Parity with the
 * legacy function is locked by computeExercise.test.ts (verbatim oracle).
 *
 * Owned by the headless TS training model (Phase 0 decision 1). The legacy
 * copy in app.js stays behind the flag and is removed in Phase 7C.
 */

export interface ComputableExercise {
  name: string
  category?: string
  base_sets?: number
  base_reps?: number
  reps_unit?: string
  reps_add_per_week?: number
  reps_max?: number
  sets_by_block?: number[]
  reps_by_block?: number[]
  hold?: string
  [key: string]: unknown
}

export interface ComputedExercise extends ComputableExercise {
  computed_sets: number
  computed_reps: number | string
  reps_display: string
  is_cardio_single: boolean
}

const TAPER_MULT = 0.7

export function computeExercise(
  ex: ComputableExercise,
  week: number,
  totalWeeks: number,
  phaseStartWeek: number,
): ComputedExercise {
  // Cool-down items use { name, hold } — pass through as-is
  if (ex.hold && !ex.reps_unit && !ex.base_reps) {
    return { ...ex, computed_sets: 1, computed_reps: ex.hold, reps_display: ex.hold, is_cardio_single: false }
  }

  const isTaper = week === totalWeeks && totalWeeks > 1

  let sets = ex.base_sets || 3
  let reps = ex.base_reps || 10

  // Block-based progression (2-week blocks within phase)
  const phaseWeek = phaseStartWeek ? week - phaseStartWeek + 1 : week
  if (Array.isArray(ex.sets_by_block) && ex.sets_by_block.length > 0) {
    const blockIdx = Math.min(Math.floor((phaseWeek - 1) / 2), ex.sets_by_block.length - 1)
    const v = ex.sets_by_block[blockIdx]
    if (v !== undefined) sets = v
  }
  if (Array.isArray(ex.reps_by_block) && ex.reps_by_block.length > 0) {
    const blockIdx = Math.min(Math.floor((phaseWeek - 1) / 2), ex.reps_by_block.length - 1)
    const v = ex.reps_by_block[blockIdx]
    if (v !== undefined) reps = v
  } else {
    const repsAdd = ex.reps_add_per_week || 0
    reps = reps + repsAdd * (week - 1)
    if (ex.reps_max) reps = Math.min(reps, ex.reps_max)
  }

  // Taper final week
  if (isTaper) {
    sets = Math.max(1, Math.round(sets * TAPER_MULT))
    reps = Math.max(1, Math.round(reps * TAPER_MULT))
  }

  // Format display string
  const unit = ex.reps_unit || 'reps'
  let repsDisplay: string
  if (unit === 'sec') repsDisplay = reps + ' sec hold'
  else if (unit === 'seconds') repsDisplay = reps + ' sec'
  else if (unit === 'min' || unit === 'minutes') repsDisplay = reps + ' min'
  else if (unit === 'mi') repsDisplay = Number(reps).toFixed(1) + ' mi'
  else if (unit === 'shuttles') repsDisplay = reps + ' shuttles'
  else if (unit === 'm') repsDisplay = reps + 'm'
  else if (unit === 'breaths') repsDisplay = reps + ' breaths'
  else if (unit === 'yards' || unit === 'yd') repsDisplay = reps + ' yd'
  else if (unit === 'steps') repsDisplay = reps + ' steps'
  else repsDisplay = reps + ' reps'

  // For cardio single-effort (sets=1), flatten display
  const isCardioSingle = ex.category === 'cardio' && sets === 1

  return { ...ex, computed_sets: sets, computed_reps: reps, reps_display: repsDisplay, is_cardio_single: isCardioSingle }
}
